import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '../../components/ui/dialog';
import {
  ArrowLeft,
  User,
  Calendar,
  FileText,
  MessageSquare,
  CheckCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Target,
  GraduationCap,
  Plane,
  Home,
  DollarSign,
  Briefcase,
  Upload,
  Loader2
} from 'lucide-react';
import { serviceRequestAPI, taskAPI } from '../../services/api';
import TaskCard from '../../components/shared/TaskCard';
import ServiceChat from '../../components/services/ServiceChat';
import FileUpload from '../../components/shared/FileUpload';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import {
  initSocket,
  onServiceRequestUpdated,
  offServiceRequestUpdated,
  onTaskCreated,
  onTaskUpdated,
  offTaskCreated,
  offTaskUpdated
} from '../../services/socket';
import { getSlugFromType } from '../../data/serviceData';

const SERVICE_TYPE_CONFIG = {
  PROFILE_ASSESSMENT: { label: 'Profile Assessment', icon: Target, color: 'from-blue-500 via-blue-600 to-indigo-600', shadow: 'shadow-blue-500/30' },
  UNIVERSITY_SHORTLISTING: { label: 'Preapplication Support', icon: GraduationCap, color: 'from-violet-500 via-purple-600 to-purple-700', shadow: 'shadow-purple-500/30' },
  APPLICATION_ASSISTANCE: { label: 'Apply University', icon: FileText, color: 'from-rose-400 via-pink-500 to-pink-600', shadow: 'shadow-pink-500/30' },
  VISA_GUIDANCE: { label: 'Visa & Interview Support', icon: Plane, color: 'from-orange-400 via-orange-500 to-amber-600', shadow: 'shadow-orange-500/30' },
  SCHOLARSHIP_SEARCH: { label: 'Ticket & Travel Support', icon: DollarSign, color: 'from-teal-400 via-green-500 to-emerald-600', shadow: 'shadow-green-500/30' },
  LOAN_ASSISTANCE: { label: 'Education Loan', icon: DollarSign, color: 'from-emerald-400 via-green-500 to-teal-600', shadow: 'shadow-emerald-500/30' },
  ACCOMMODATION_HELP: { label: 'Find Accommodation', icon: Home, color: 'from-pink-400 via-rose-500 to-pink-600', shadow: 'shadow-pink-500/30' },
  PRE_DEPARTURE_ORIENTATION: { label: 'Find Jobs Abroad', icon: Briefcase, color: 'from-amber-400 via-orange-500 to-orange-600', shadow: 'shadow-orange-500/30' }
};

const STATUS_CONFIG = {
  PENDING_ADMIN_ASSIGNMENT: { label: 'Pending Assignment', color: 'bg-slate-100 text-slate-700', icon: Clock, progress: 10 },
  ASSIGNED: { label: 'Assigned', color: 'bg-blue-100 text-blue-700', icon: User, progress: 30 },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-amber-100 text-amber-700', icon: RefreshCw, progress: 60 },
  ON_HOLD: { label: 'On Hold', color: 'bg-orange-100 text-orange-700', icon: AlertCircle, progress: 50 },
  COMPLETED: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, progress: 100 },
  CANCELLED: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: AlertCircle, progress: 0 }
};

export default function ServiceDetailPage() {
  const { serviceRequestId } = useParams();
  const navigate = useNavigate();
  const [redirectAttempted, setRedirectAttempted] = useState(false);

  // Try to redirect to the new service hub page
  useEffect(() => {
    if (redirectAttempted) return;
    (async () => {
      try {
        const res = await serviceRequestAPI.getById(serviceRequestId);
        const sr = res.serviceRequest;
        if (sr?.serviceType) {
          const slug = getSlugFromType(sr.serviceType);
          if (slug) {
            navigate(`/dashboard/services/${slug}`, { replace: true });
            return;
          }
        }
      } catch {
        // Fallback to existing page
      }
      setRedirectAttempted(true);
    })();
  }, [serviceRequestId, navigate, redirectAttempted]);

  const [serviceRequest, setServiceRequest] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Task submission modal
  const [submitTaskOpen, setSubmitTaskOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [submissionText, setSubmissionText] = useState('');
  const [submissionFiles, setSubmissionFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Fetch service request and tasks
  const fetchData = useCallback(async () => {
    try {
      const [requestRes, tasksRes] = await Promise.all([
        serviceRequestAPI.getById(serviceRequestId),
        taskAPI.getAll({ serviceRequestId })
      ]);
      setServiceRequest(requestRes.serviceRequest);
      setTasks(tasksRes.tasks?.filter(t => t.serviceRequestId === serviceRequestId) || []);
    } catch (error) {
      toast.error('Failed to load service details');
      navigate('/dashboard/track');
    } finally {
      setLoading(false);
    }
  }, [serviceRequestId, navigate]);

  useEffect(() => {
    fetchData();
    initSocket();

    // Socket listeners for real-time updates
    onServiceRequestUpdated((updated) => {
      if (updated.serviceRequestId === serviceRequestId) {
        setServiceRequest(updated);
        toast.info('Service request updated');
      }
    });

    onTaskCreated((newTask) => {
      if (newTask.serviceRequestId === serviceRequestId) {
        setTasks(prev => [newTask, ...prev]);
        toast.info(`New task: ${newTask.title}`);
      }
    });

    onTaskUpdated((updated) => {
      setTasks(prev => prev.map(t =>
        t.taskId === updated.taskId ? updated : t
      ));
    });

    return () => {
      offServiceRequestUpdated();
      offTaskCreated();
      offTaskUpdated();
    };
  }, [fetchData, serviceRequestId]);

  const handleSubmitTask = (task) => {
    setSelectedTask(task);
    setSubmissionText('');
    setSubmissionFiles([]);
    setSubmitTaskOpen(true);
  };

  const handleTaskSubmission = async () => {
    if (!submissionText.trim() && submissionFiles.length === 0) {
      toast.error('Please provide text or upload files');
      return;
    }

    setSubmitting(true);
    try {
      await taskAPI.submit(selectedTask.taskId, submissionText, submissionFiles);
      toast.success('Task submitted successfully!');
      setSubmitTaskOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUploadComplete = (uploadedFiles) => {
    setSubmissionFiles(prev => [...prev, ...uploadedFiles]);
  };

  if (loading || !serviceRequest) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-500 font-medium">Loading service details...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const serviceConfig = SERVICE_TYPE_CONFIG[serviceRequest.serviceType] || {};
  const statusConfig = STATUS_CONFIG[serviceRequest.status] || {};
  const ServiceIcon = serviceConfig.icon || FileText;
  const counselor = serviceRequest.counselor || serviceRequest.assignedCounselorInfo;
  const agent = serviceRequest.agent || serviceRequest.assignedAgentInfo;

  const pendingTasks = tasks.filter(t =>
    ['PENDING', 'IN_PROGRESS', 'REVISION_REQUIRED'].includes(t.status)
  );
  const completedTasks = tasks.filter(t => t.status === 'COMPLETED');

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/dashboard/track')} className="mb-2 rounded-xl hover:bg-slate-100 text-slate-600">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Track Services
        </Button>

        {/* Header Card */}
        <Card className={`relative overflow-hidden p-8 bg-gradient-to-br ${serviceConfig.color || 'from-slate-400 to-slate-500'} text-white rounded-3xl shadow-2xl ${serviceConfig.shadow || ''} border-0`}>
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-44 h-44 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 right-24 w-28 h-28 bg-white/5 rounded-full translate-y-1/2" />
          <div className="absolute top-1/2 right-12 w-20 h-20 bg-white/10 rounded-2xl rotate-12 -translate-y-1/2" />
          <div className="absolute bottom-4 right-4 w-12 h-12 bg-white/5 rounded-xl rotate-45" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-xl shadow-black/10 ring-2 ring-white/20">
                <ServiceIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-outfit font-bold tracking-tight">
                  {serviceConfig.label || serviceRequest.serviceType}
                </h1>
                <p className="text-white/60 text-sm mt-1.5 font-medium">
                  Request ID: {serviceRequest.serviceRequestId.slice(0, 8)}...
                </p>
              </div>
            </div>
            <Badge className="bg-white/20 text-white border-white/30 text-sm px-5 py-2.5 w-fit rounded-full backdrop-blur-md font-semibold shadow-lg shadow-black/5">
              {statusConfig.label}
            </Badge>
          </div>

          <div className="relative z-10 mt-8">
            <div className="flex justify-between text-sm text-white/70 mb-2.5">
              <span className="font-medium">Progress</span>
              <span className="font-bold text-white">{statusConfig.progress || 0}%</span>
            </div>
            <Progress value={statusConfig.progress || 0} className="h-2.5 bg-white/20 rounded-full" />
          </div>
        </Card>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4 bg-slate-100 p-1 rounded-xl">
                <TabsTrigger value="overview" className="rounded-lg">Overview</TabsTrigger>
                <TabsTrigger value="tasks" className="rounded-lg">Tasks ({tasks.length})</TabsTrigger>
                <TabsTrigger value="chat" className="rounded-lg">Chat</TabsTrigger>
                <TabsTrigger value="history" className="rounded-lg">History</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-4 mt-4">
                {/* Progress Summary */}
                <Card className="p-5 rounded-xl border border-slate-100 shadow-sm">
                  <h3 className="font-semibold text-slate-900 mb-4">Progress Summary</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-amber-50/80 rounded-xl border border-amber-100">
                      <p className="text-2xl font-bold text-amber-600">{pendingTasks.length}</p>
                      <p className="text-sm text-slate-500 font-medium">Pending Tasks</p>
                    </div>
                    <div className="text-center p-4 bg-emerald-50/80 rounded-xl border border-emerald-100">
                      <p className="text-2xl font-bold text-emerald-600">{completedTasks.length}</p>
                      <p className="text-sm text-slate-500 font-medium">Completed</p>
                    </div>
                    <div className="text-center p-4 bg-sky-50/80 rounded-xl border border-sky-100">
                      <p className="text-2xl font-bold text-sky-600">{tasks.length}</p>
                      <p className="text-sm text-slate-500 font-medium">Total Tasks</p>
                    </div>
                  </div>
                </Card>

                {/* Pending Tasks Preview */}
                {pendingTasks.length > 0 && (
                  <Card className="p-5 rounded-xl border border-amber-100/80 shadow-sm bg-gradient-to-br from-amber-50/40 to-white">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                      </div>
                      Tasks Requiring Action
                    </h3>
                    <div className="space-y-3">
                      {pendingTasks.slice(0, 3).map(task => (
                        <TaskCard
                          key={task.taskId}
                          task={task}
                          variant="compact"
                          onSubmit={() => handleSubmitTask(task)}
                        />
                      ))}
                      {pendingTasks.length > 3 && (
                        <Button
                          variant="outline"
                          className="w-full rounded-xl border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300 transition-all"
                          onClick={() => setActiveTab('tasks')}
                        >
                          View All {pendingTasks.length} Pending Tasks
                        </Button>
                      )}
                    </div>
                  </Card>
                )}

                {/* Notes */}
                {serviceRequest.notes?.filter(n => !n.isInternal).length > 0 && (
                  <Card className="p-5 rounded-xl border border-slate-100 shadow-sm">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center">
                        <MessageSquare className="w-4 h-4 text-sky-600" />
                      </div>
                      Messages
                    </h3>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {serviceRequest.notes
                        .filter(n => !n.isInternal)
                        .slice(0, 5)
                        .map((note, idx) => (
                          <div key={idx} className="p-3.5 bg-slate-50/80 rounded-xl border border-slate-100">
                            <p className="text-sm text-slate-700">{note.text}</p>
                            <p className="text-xs text-slate-400 mt-1.5 font-medium">
                              {format(new Date(note.addedAt), 'MMM d, yyyy h:mm a')}
                            </p>
                          </div>
                        ))}
                    </div>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="tasks" className="mt-4">
                {tasks.length === 0 ? (
                  <Card className="p-10 text-center rounded-xl border border-slate-100 shadow-sm">
                    <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                      <FileText className="w-8 h-8 text-slate-300" />
                    </div>
                    <p className="text-slate-500 font-semibold">No tasks assigned yet</p>
                    <p className="text-sm text-slate-400 mt-1.5 max-w-xs mx-auto">
                      Your counselor will create tasks as you progress through the service.
                    </p>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {tasks.map(task => (
                      <TaskCard
                        key={task.taskId}
                        task={task}
                        onSubmit={() => handleSubmitTask(task)}
                        onView={() => handleSubmitTask(task)}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="chat" className="mt-4">
                <ServiceChat
                  serviceRequestId={serviceRequest.serviceRequestId}
                  counselor={counselor}
                  agent={agent}
                />
              </TabsContent>

              <TabsContent value="history" className="mt-4">
                <Card className="p-5 rounded-xl border border-slate-100 shadow-sm">
                  {serviceRequest.statusHistory?.length > 0 ? (
                    <div className="space-y-4">
                      {serviceRequest.statusHistory.map((entry, idx) => (
                        <div key={idx} className="flex gap-4 p-3 hover:bg-slate-50/60 rounded-xl transition-colors">
                          <div className="w-10 h-10 bg-gradient-to-br from-sky-100 to-blue-50 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                            <RefreshCw className="w-4 h-4 text-sky-600" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-slate-800">Status changed to</p>
                              <Badge className={`${STATUS_CONFIG[entry.status]?.color || 'bg-slate-100'} rounded-full text-xs px-2.5`}>
                                {STATUS_CONFIG[entry.status]?.label || entry.status}
                              </Badge>
                            </div>
                            {entry.note && <p className="text-sm text-slate-500 mt-1">{entry.note}</p>}
                            <p className="text-xs text-slate-400 mt-1.5 font-medium">
                              {format(new Date(entry.changedAt), 'MMM d, yyyy h:mm a')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10">
                      <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                        <Clock className="w-8 h-8 text-slate-300" />
                      </div>
                      <p className="text-slate-500 font-medium">No status history yet</p>
                    </div>
                  )}
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Assigned Personnel Card */}
            <Card className="p-5 rounded-xl border border-slate-100 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4">Your Support Team</h3>

              {counselor ? (
                <div className="flex items-center gap-3 p-3 bg-sky-50/80 rounded-xl border border-sky-100 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-semibold shadow-lg shadow-sky-500/20 ring-2 ring-white">
                    {counselor.firstName?.[0]}{counselor.lastName?.[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{counselor.firstName} {counselor.lastName}</p>
                    <p className="text-sm text-slate-500">Counselor</p>
                    <p className="text-xs text-slate-400">{counselor.email}</p>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-100 text-center mb-3">
                  <Clock className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Awaiting counselor assignment</p>
                </div>
              )}

              {agent && (
                <div className="flex items-center gap-3 p-3 bg-emerald-50/80 rounded-xl border border-emerald-100">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center text-white font-semibold shadow-lg shadow-emerald-500/20 ring-2 ring-white">
                    {agent.firstName?.[0]}{agent.lastName?.[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{agent.firstName} {agent.lastName}</p>
                    <p className="text-sm text-slate-500">Agent</p>
                  </div>
                </div>
              )}
            </Card>

            {/* Timeline Card */}
            <Card className="p-5 rounded-xl border border-slate-100 shadow-sm">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-violet-600" />
                </div>
                Timeline
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3 p-2.5 bg-slate-50/80 rounded-lg">
                  <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className="text-slate-500">Applied:</span>
                  <span className="font-semibold text-slate-800">
                    {format(new Date(serviceRequest.appliedAt || serviceRequest.createdAt), 'MMM d, yyyy')}
                  </span>
                </div>
                {serviceRequest.assignedAt && (
                  <div className="flex items-center gap-3 p-2.5 bg-sky-50/60 rounded-lg">
                    <User className="w-4 h-4 text-sky-500 flex-shrink-0" />
                    <span className="text-slate-500">Assigned:</span>
                    <span className="font-semibold text-slate-800">
                      {format(new Date(serviceRequest.assignedAt), 'MMM d, yyyy')}
                    </span>
                  </div>
                )}
                {serviceRequest.completedAt && (
                  <div className="flex items-center gap-3 p-2.5 bg-emerald-50/60 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-slate-500">Completed:</span>
                    <span className="font-semibold text-slate-800">
                      {format(new Date(serviceRequest.completedAt), 'MMM d, yyyy')}
                    </span>
                  </div>
                )}
              </div>
            </Card>

            {/* Quick Actions */}
            {counselor && (
              <Card className="p-5 rounded-xl border border-slate-100 shadow-sm">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Target className="w-4 h-4 text-amber-600" />
                  </div>
                  Quick Actions
                </h3>
                <div className="space-y-2.5">
                  <Button
                    variant="outline"
                    className="w-full justify-start rounded-xl border-slate-100 bg-white hover:bg-sky-50 hover:border-sky-200 hover:shadow-sm transition-all duration-200 group"
                    onClick={() => setActiveTab('chat')}
                  >
                    <div className="w-7 h-7 rounded-lg bg-sky-100 flex items-center justify-center mr-2.5 group-hover:bg-sky-200 transition-colors">
                      <MessageSquare className="w-3.5 h-3.5 text-sky-600" />
                    </div>
                    <span className="text-slate-700 font-medium text-sm">Message Counselor</span>
                  </Button>
                  {pendingTasks.length > 0 && (
                    <Button
                      variant="outline"
                      className="w-full justify-start rounded-xl border-slate-100 bg-white hover:bg-amber-50 hover:border-amber-200 hover:shadow-sm transition-all duration-200 group"
                      onClick={() => setActiveTab('tasks')}
                    >
                      <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center mr-2.5 group-hover:bg-amber-200 transition-colors">
                        <FileText className="w-3.5 h-3.5 text-amber-600" />
                      </div>
                      <span className="text-slate-700 font-medium text-sm">View Pending Tasks ({pendingTasks.length})</span>
                    </Button>
                  )}
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Task Submission Modal */}
        <Dialog open={submitTaskOpen} onOpenChange={setSubmitTaskOpen}>
          <DialogContent className="max-w-2xl rounded-2xl p-0 flex flex-col max-h-[85vh]">
            {selectedTask && (
              <>
                <DialogHeader className="px-6 pt-6 pb-3 border-b shrink-0">
                  <DialogTitle className="text-xl font-bold text-slate-900">{selectedTask.title}</DialogTitle>
                  <DialogDescription className="text-slate-500">
                    {selectedTask.description || 'Complete this task by providing the required information.'}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 px-6 py-4 overflow-y-auto flex-1">
                  {selectedTask.instructions && (
                    <div className="p-4 bg-sky-50/80 rounded-xl border border-sky-100">
                      <p className="text-sm font-semibold text-sky-800 mb-1">Instructions</p>
                      <p className="text-sm text-sky-700">{selectedTask.instructions}</p>
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-2 block">Your Response</label>
                    <Textarea
                      placeholder="Enter your response here..."
                      value={submissionText}
                      onChange={(e) => setSubmissionText(e.target.value)}
                      rows={4}
                      className="rounded-xl border-slate-200 focus:border-sky-300 focus:ring-sky-200"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-700 mb-2 block">Upload Files (Optional)</label>
                    <FileUpload
                      onUploadComplete={handleFileUploadComplete}
                      maxFiles={5}
                      folder="task-submissions"
                    />
                  </div>

                  {submissionFiles.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Uploaded Files</p>
                      <div className="flex flex-wrap gap-2">
                        {submissionFiles.map((file, idx) => (
                          <Badge key={idx} variant="secondary" className="rounded-lg bg-slate-100 text-slate-700 px-3 py-1">
                            {file.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter className="px-6 py-4 border-t shrink-0 bg-white rounded-b-2xl">
                  <Button variant="outline" onClick={() => setSubmitTaskOpen(false)} className="rounded-xl border-slate-200 hover:bg-slate-50">
                    Cancel
                  </Button>
                  <Button onClick={handleTaskSubmission} disabled={submitting} className="rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 shadow-md shadow-sky-500/20">
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Submit Task
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
